import React, { useEffect, useState } from 'react';
import { Modal, Button, Form, Alert, Badge } from 'react-bootstrap';
import { functions } from '../../firebase';
import { httpsCallable } from 'firebase/functions';

// Staff sender for the enrollment-gated claim flow (fall model §3g,
// 2026-07-15). Replaces the retired temp-password invite: one claim link goes
// to ONE parent email and covers ALL selected students (one family account,
// student sub-profiles — §3g account shape). The link lets the family set
// their own password (or use Google); no CYV-generated credential exists.
// Both callables are sendEmail-permission-gated and write the portalInvites
// record server-side (client writes are rules-denied).
//
// TWO KINDS (customer principal model P1, docs/customer-principal-model.md §2):
//   primary  — createPortalClaim. Binds the FIRST parent and writes
//              families.primaryGuardian*. Bound to the selected students.
//   guardian — createGuardianInvite. Adds a SECOND parent to a family that is
//              already claimed. Bound to the FAMILY, grants the same reach, and
//              never touches primaryGuardian* (that stays the billing and
//              primary-contact designation).
//   student  — createStudentInvite. A STUDENT's OWN sign-in (P2a/P2b). Bound to
//              ONE student, never a family, and it reads rather than writes:
//              the account can see the student's own schedule and song notes
//              and touches neither the family doc nor the student doc.
// Issuance is staff-only in v1 by decision — there is deliberately no
// parent-facing "invite my co-parent" surface yet.
const studentNameOf = (s) =>
	!s
		? ''
		: s.fullName ||
			`${s.firstName || ''} ${s.lastName || ''}`.trim() ||
			'Student';

const SendPortalInvite = ({ show, onHide, students, onSuccess }) => {
	const [mode, setMode] = useState('primary');
	const [sending, setSending] = useState(false);
	const [recipientEmail, setRecipientEmail] = useState('');
	const [recipientName, setRecipientName] = useState('');
	const [customMessage, setCustomMessage] = useState('');
	const [error, setError] = useState('');
	const [sendResult, setSendResult] = useState(null);

	const isGuardian = mode === 'guardian';
	const isStudent = mode === 'student';

	// A guardian invite is family-scoped, so it needs a family to point at.
	// Derived from the students on screen rather than asking staff to know an
	// id. If the selection spans families there is no single answer and the
	// mode is refused rather than guessed.
	const familyIds = [
		...new Set((students || []).map((s) => s.familyId).filter(Boolean)),
	];
	const familyId = familyIds.length === 1 ? familyIds[0] : null;
	const guardianBlockedReason =
		familyIds.length === 0
			? 'These students are not linked to a family account yet — send the first parent a setup link instead.'
			: familyIds.length > 1
				? 'These students belong to different family accounts. Open one student to invite a second parent.'
				: null;

	/* A PRIMARY claim covering students from more than one family is never
	   right: the claim transaction unifies every selected student's family into
	   ONE families doc with the recipient as primary guardian and billing
	   contact for all of them. Nothing server-side forbids it for UNCLAIMED
	   families (the double-bind guard only protects claimed ones; the
	   12-student cap catches only large selections), so this modal is the
	   guard. Nearly fired 2026-08-22: select-all across 29 families produced a
	   sendable form prefilled with one real parent's email. Students carrying
	   NO familyId are deliberately allowed alongside ONE family — backfilling
	   legacy students into the family is a designed claim behavior. */
	const primaryBlockedReason =
		familyIds.length > 1
			? `These students span ${familyIds.length} different family accounts, and one setup link would merge them into ONE account owned by one parent. Select one family's students at a time.`
			: null;

	/* A student invite binds ONE student (performCreateStudentInvite takes a
	   studentId, not a list), so a multi-select has no single answer and is
	   refused rather than guessed — the same shape as the guardian rule above.
	   The family must also exist; whether it is CLAIMED and active is a families
	   read this modal does not have, so that half is left to the server, which
	   returns a family-readable message ("Send the parent a portal invite
	   first"). */
	const soleStudent = (students || []).length === 1 ? students[0] : null;
	const studentBlockedReason =
		(students || []).length === 0
			? 'Select a student first.'
			: !soleStudent
				? 'A student account is for one student. Open a single student to invite them.'
				: !soleStudent.familyId
					? 'This student is not linked to a family account yet — send the parent a setup link first.'
					: null;

	// Prefill the paying-parent contact from the first student that has one
	// (legacy MMS shape: student.parent1). Staff can overtype freely.
	//
	// Deliberately NOT prefilled for a guardian invite: parent1 is the address
	// that already claimed, so prefilling it would hand staff a form that fails
	// with "that is already the primary parent" on every send.
	useEffect(() => {
		if (!show) return;
		if (isGuardian) {
			/* Never prefilled: parent1 already claimed, so prefilling it would
			   hand staff a form that fails with "already the primary parent". */
			setRecipientEmail('');
			setRecipientName('');
			return;
		}
		if (isStudent) {
			/* Prefilled from the STUDENT'S OWN record (students.email), never
			   from parent1. The server REFUSES a student invite whose email
			   matches families.primaryGuardianEmail (H8), because claiming it
			   would merge role:'student' onto the parent's users doc and demote
			   the adult into a hybrid principal — so a student record that
			   carries the parent's address is left blank rather than offered.
			   Until 2026-09-14 this branch prefilled nothing at all, and staff
			   retyped an address the record already held (Anthony's ask). */
			const s0 = students?.[0];
			const own = (s0?.email || '').trim();
			const parentEmail = (s0?.parent1?.email || '').trim().toLowerCase();
			const usable = own && own.toLowerCase() !== parentEmail ? own : '';
			setRecipientEmail(usable);
			setRecipientName(studentNameOf(s0));
			return;
		}
		const withParent = (students || []).find((s) => s.parent1?.email);
		const p1 = withParent?.parent1;
		setRecipientEmail(p1?.email || '');
		setRecipientName(
			p1 ? `${p1.firstName || ''} ${p1.lastName || ''}`.trim() : ''
		);
	}, [show, students, isGuardian, isStudent]);


	const handleSend = async () => {
		setError('');
		const who = isStudent ? 'Student' : 'Parent';
		if (!recipientEmail.trim()) return setError(`${who} email is required.`);
		if (!recipientName.trim()) return setError(`${who} name is required.`);
		if (isGuardian && !familyId) return setError(guardianBlockedReason);
		if (isStudent && studentBlockedReason) return setError(studentBlockedReason);
		if (!isGuardian && !isStudent && primaryBlockedReason)
			return setError(primaryBlockedReason);

		setSending(true);
		try {
			const callable = httpsCallable(
				functions,
				isGuardian
					? 'createGuardianInvite'
					: isStudent
						? 'createStudentInvite'
						: 'createPortalClaim'
			);
			const { data } = await callable({
				...(isGuardian
					? { familyId }
					: isStudent
						? { studentId: soleStudent.id }
						: { studentIds: students.map((s) => s.id) }),
				recipientEmail: recipientEmail.trim(),
				recipientName: recipientName.trim(),
				customMessage: customMessage.trim() || null,
				origin: window.location.origin,
			});
			setSendResult(data);
			if (onSuccess) {
				setTimeout(() => onSuccess(data), 2500);
			}
		} catch (err) {
			console.error('Failed to send invite:', err);
			setError(err.message || 'Failed to send the invite. Please try again.');
		} finally {
			setSending(false);
		}
	};

	const handleClose = () => {
		setSendResult(null);
		setCustomMessage('');
		setError('');
		setMode('primary');
		onHide();
	};

	return (
		<Modal show={show} onHide={handleClose} size='lg'>
			<Modal.Header closeButton>
				<Modal.Title>
					<i className='fa-sharp fa-solid fa-paper-plane me-2'></i>
					{isGuardian
						? 'Invite a Second Parent'
						: isStudent
							? 'Invite a Student'
							: 'Send Portal Account Invite'}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body>
				{!sendResult ? (
					<>
						<Form.Group className='mb-3'>
							<Form.Label className='d-block'>
								<strong>What kind of invite?</strong>
							</Form.Label>
							<Form.Check
								inline
								type='radio'
								id='invite-mode-primary'
								name='invite-mode'
								label='Set up a new account'
								checked={!isGuardian}
								onChange={() => setMode('primary')}
								disabled={sending}
							/>
							<Form.Check
								inline
								type='radio'
								id='invite-mode-guardian'
								name='invite-mode'
								label='Add a second parent'
								checked={isGuardian}
								onChange={() => setMode('guardian')}
								disabled={sending}
							/>
							<Form.Check
								inline
								type='radio'
								id='invite-mode-student'
								name='invite-mode'
								label='Give the student their own sign-in'
								checked={isStudent}
								onChange={() => setMode('student')}
								disabled={sending}
							/>
						</Form.Group>

						{isStudent ? (
							<Alert variant={studentBlockedReason ? 'warning' : 'info'}>
								<i className='fa-sharp fa-solid fa-info-circle me-2'></i>
								{studentBlockedReason || (
									<>
										The student gets their own sign-in showing{' '}
										<strong>their own</strong> schedule and song notes —
										not a sibling's, and not the family's payments. It
										adds an account; it changes nothing about the
										family's. Use the student's own email address:{' '}
										<strong>
											a parent's address cannot be reused here
										</strong>
										, and the send will be refused if it is. Link expires
										in 7 days.
									</>
								)}
							</Alert>
						) : isGuardian ? (
							<Alert variant={guardianBlockedReason ? 'warning' : 'info'}>
								<i className='fa-sharp fa-solid fa-info-circle me-2'></i>
								{guardianBlockedReason || (
									<>
										The second parent gets their own sign-in and sees the
										same schedule, lessons and notes. Nothing about the
										family's existing account, billing or primary contact
										changes. Link expires in 7 days.
									</>
								)}
							</Alert>
						) : (
							<Alert variant={primaryBlockedReason ? 'warning' : 'info'}>
								<i className='fa-sharp fa-solid fa-info-circle me-2'></i>
								{primaryBlockedReason || (
									<>
										The parent gets a personal{' '}
										<strong>account setup link</strong> (expires in 7 days).
										They choose their own password — no temporary
										credentials are created or emailed. One family account
										covers all the students selected below.
									</>
								)}
							</Alert>
						)}

						{error && <Alert variant='danger'>{error}</Alert>}

						<Form>
							<Form.Group className='mb-3'>
								<Form.Label>
									<strong>
										{isGuardian
											? 'Students on this family account:'
											: isStudent
												? 'This account is for:'
												: 'Students on this account:'}
									</strong>{' '}
									{students.length}
								</Form.Label>
								<div
									className='border rounded p-2'
									style={{ maxHeight: '150px', overflowY: 'auto' }}>
									{students.map((student, idx) => (
										<Badge key={idx} bg='primary' className='me-2 mb-2'>
											{studentNameOf(student)}
										</Badge>
									))}
								</div>
							</Form.Group>

							<Form.Group className='mb-3'>
								<Form.Label>
									<strong>
										{isStudent ? 'Student Name' : 'Parent Name'}
									</strong>
								</Form.Label>
								<Form.Control
									type='text'
									placeholder='Who the invite greets'
									value={recipientName}
									onChange={(e) => setRecipientName(e.target.value)}
									disabled={sending}
								/>
							</Form.Group>

							<Form.Group className='mb-3'>
								<Form.Label>
									<strong>
										{isStudent ? 'Student Email' : 'Parent Email'}
									</strong>
								</Form.Label>
								<Form.Control
									type='email'
									placeholder='Where the setup link is sent'
									value={recipientEmail}
									onChange={(e) => setRecipientEmail(e.target.value)}
									disabled={sending}
								/>
								<Form.Text className='text-muted'>
									{isGuardian
										? "The second parent's own address — not the one that already set up the account."
										: isStudent
											? "The student's own address, from their record. A parent's address is refused here."
											: "The account is tied to this email — use the paying parent's address."}
								</Form.Text>
							</Form.Group>

							<Form.Group className='mb-3'>
								<Form.Label>
									<strong>Custom Message</strong> (Optional)
								</Form.Label>
								<Form.Control
									as='textarea'
									rows={3}
									placeholder='Add a personal message to include in the email...'
									value={customMessage}
									onChange={(e) => setCustomMessage(e.target.value)}
									disabled={sending}
								/>
							</Form.Group>
						</Form>
					</>
				) : (
					<>
						<Alert variant='success'>
							<i className='fa-sharp fa-solid fa-check-circle me-2'></i>
							<strong>Invite sent</strong> to {recipientName} (
							{recipientEmail}){' '}
							{isGuardian
								? 'to join this family account.'
								: `covering ${(sendResult.studentNames || []).join(', ')}.`}
						</Alert>
						{sendResult.redirected && (
							<Alert variant='warning' className='mb-0'>
								<i className='fa-sharp fa-solid fa-triangle-exclamation me-2'></i>
								Production email is OFF — the invite was redirected to
								the test inbox instead of the parent.
							</Alert>
						)}
					</>
				)}
			</Modal.Body>
			<Modal.Footer>
				<Button variant='secondary' onClick={handleClose} disabled={sending}>
					{sendResult ? 'Close' : 'Cancel'}
				</Button>
				{!sendResult && (
					<Button
						variant='primary'
						onClick={handleSend}
						disabled={
							sending ||
							students.length === 0 ||
							(isGuardian && !familyId) ||
							(!isGuardian && !isStudent && !!primaryBlockedReason)
						}>
						{sending ? (
							<>
								<i className='fa-sharp fa-solid fa-spinner fa-spin me-2'></i>
								Sending...
							</>
						) : (
							<>
								<i className='fa-sharp fa-solid fa-paper-plane me-2'></i>
								{isGuardian ? 'Send Invite' : 'Send Setup Link'}
							</>
						)}
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default SendPortalInvite;
